import { Modal, Button, Group, Select, Stack, Text, Textarea } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configApi } from "../api/config.js";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import type { PipelineMode } from "@cas/shared";

interface Props {
  opened: boolean;
  onClose: () => void;
  currentMode: PipelineMode;
}

export function ModeChangeDialog({ opened, onClose, currentMode }: Props) {
  const [target, setTarget] = useState<PipelineMode | null>(null);
  const [reason, setReason] = useState("");
  const qc = useQueryClient();

  const mut = useMutation({
    mutationFn: (mode: PipelineMode) => configApi.setMode(mode, reason || undefined),
    onSuccess: () => {
      notifications.show({ title: "Mode changed", message: `Now: ${target}`, color: "green" });
      qc.invalidateQueries({ queryKey: ["config"] });
      onClose();
    },
    onError: (e) => notifications.show({ title: "Failed", message: String(e), color: "red" }),
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Change pipeline mode" centered>
      <Stack>
        <Text size="sm">Current mode: <strong>{currentMode}</strong></Text>
        <Select
          label="New mode"
          data={[
            { value: "V6_LIVE",   label: "V6_LIVE — V6 handles mail, V7 dormant" },
            { value: "V7_SHADOW", label: "V7_SHADOW — V6 lives, V7 logs decisions only" },
            { value: "V7_LIVE",   label: "V7_LIVE — V7 acts on mail, V6 dormant" },
          ].filter(o => o.value !== currentMode)}
          value={target}
          onChange={(v) => setTarget(v as PipelineMode | null)}
        />
        <Textarea label="Reason (audit log)" minRows={2}
          placeholder="Why are you flipping the mode?"
          value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        <Text size="xs" c="dimmed">
          The change is recorded in App Insights (event PipelineModeChanged).
          Rollback is the same dialog with the old mode picked.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button disabled={!target} loading={mut.isPending}
                  onClick={() => target && mut.mutate(target)}>
            Confirm change
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
